import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackBar } from '../../../FlowBuilder/Common/SnackBar/index.tsx';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ReportActions } from '../../../../Services/_actions/Report/report.action.js';
import CommonTable from '../../../Shared/Tables/TableV1.tsx';
import { dateFormate } from '../../../../utils/helper.jsx';
import DynamicBreadcrumbs from '../../../Shared/Breadcrumb/index.tsx';
import HomeIcon from '@mui/icons-material/Home';

// ---- API shapes ----
type TriggerApiItem = {
  id: string;
  trigger_type: string; // "schedule"
  schedule_cron?: string; // "0 0 * * *"
  schedule_timezone?: string; // "Asia/Kolkata"
  schedule_description?: string; // "Daily at 00:00"
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  next_run_at?: string | null; // ISO, e.g. "2025-09-25T00:00:00"
  last_run_at?: string | null;

  delivery_methods?: string[]; // ["email"]
  stakeholder_emails?: string[]; // ["a@b.com"]
  email_subject?: string | null;
  email_format?: string | null; // "html" | "text"
  attachment_formats?: string[]; // ["html"]

  whatsapp_phone_numbers?: string[];
  whatsapp_template_id?: string | null;
  whatsapp_template_params?: Record<string, any>;
  whatsapp_instance_id?: string | null;

  notification_summary?: {
    total_email_recipients?: number;
    total_whatsapp_recipients?: number;
    has_whatsapp_template?: boolean;
    notification_channels?: string[];
  };
};

type TriggerListResponseData = {
  template_id: string;
  template_name: string;
  triggers: TriggerApiItem[];
  total_triggers: number;
  active_triggers: number;
};

const TriggerListPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { templateName?: string } };
  const templateNameFromRoute = location?.state?.templateName;

  const actionsRef = useRef(ReportActions());
  const reportActions = actionsRef.current;
  const { showSnackBar } = useSnackBar();

  const [loading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<TriggerApiItem[]>([]);
  const [templateName, setTemplateName] = useState<string>(templateNameFromRoute || 'Template');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [activeCount, setActiveCount] = useState<number>(0);

  // Guards to avoid dependency arrays
  const initializedRef = useRef<boolean>(false);
  const prevTemplateIdRef = useRef<string | undefined>(undefined);

  const fetchTriggers = async (tid?: string) => {
    if (!tid) return;
    setLoading(true);
    try {
      const res = await reportActions.getTemplateTriggers(tid);

      // Accept either raw array or wrapped { status, data: { ... } }
      let payload: TriggerListResponseData | null = null;

      // case: axios-like { data: { data: {...wrapped} } }
      if (res?.data?.data?.triggers) {
        payload = res.data.data as TriggerListResponseData;
      }
      // case: axios-like { data: {...maybe wrapped} }
      else if (res?.data?.triggers) {
        payload = res.data as TriggerListResponseData;
      }
      // case: fetch-like already the object
      else if (res?.triggers) {
        payload = res as TriggerListResponseData;
      }
      // case: raw array fallback (older)
      else if (Array.isArray(res)) {
        payload = {
          template_id: tid,
          template_name: templateNameFromRoute || 'Template',
          triggers: res as TriggerApiItem[],
          total_triggers: (res as TriggerApiItem[]).length,
          active_triggers: (res as TriggerApiItem[]).filter((t) => t.is_active).length,
        };
      }

      if (!payload) {
        setRows([]);
        setTemplateName(templateNameFromRoute || 'Template');
        setTotalCount(0);
        setActiveCount(0);
        return;
      }

      setTemplateName(payload.template_name || templateNameFromRoute || 'Template');
      setRows(Array.isArray(payload.triggers) ? payload.triggers : []);
      setTotalCount(Number(payload.total_triggers || 0));
      setActiveCount(Number(payload.active_triggers || 0));
    } catch (e: any) {
      showSnackBar('Failed to fetch triggers', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Effect without dependency array; guarded to run on mount and when templateId changes
  useEffect(() => {
    const currentId = templateId;
    if (!initializedRef.current) {
      initializedRef.current = true;
      prevTemplateIdRef.current = currentId;
      fetchTriggers(currentId);
      return;
    }
    if (prevTemplateIdRef.current !== currentId) {
      prevTemplateIdRef.current = currentId;
      fetchTriggers(currentId);
    }
  });

  // ----- Columns for CommonTable -----
  const columns = [
    {
      accessorKey: 'trigger_type',
      header: 'trigger_type',
      Cell: ({ row }: any) => (
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="body2" fontWeight={600}>
            {row.original.trigger_type || '—'}
          </Typography>
          <Chip
            size="small"
            label={row.original.is_active ? 'Active' : 'Inactive'}
            color={row.original.is_active ? 'success' : 'default'}
            variant={row.original.is_active ? 'filled' : 'outlined'}
          />
        </Stack>
      ),
    },
    {
      accessorKey: 'schedule_description',
      header: 'schedule_description',
      Cell: ({ cell }: any) => (
        <Typography variant="body2" color="text.secondary">
          {cell.getValue() || '—'}
        </Typography>
      ),
    },
    {
      accessorKey: 'schedule_cron',
      header: 'schedule_cron',
      Cell: ({ cell }: any) => (cell.getValue() ? <code>{cell.getValue()}</code> : '—'),
    },
    {
      accessorKey: 'schedule_timezone',
      header: 'schedule_timezone',
      Cell: ({ cell }: any) => <Typography variant="body2">{cell.getValue() || '—'}</Typography>,
    },
    {
      accessorKey: 'next_run_at',
      header: 'next_run_at',
      Cell: ({ cell }: any) => (
        <Typography variant="body2">
          {cell.getValue() ? dateFormate(cell.getValue(), 'DD-MM-YYYY, hh:mm A') : '—'}
        </Typography>
      ),
    },
    {
      accessorKey: 'last_run_at',
      header: 'last_run_at',
      Cell: ({ cell }: any) => (
        <Typography variant="body2">
          {cell.getValue() ? dateFormate(cell.getValue(), 'DD-MM-YYYY, hh:mm A') : '—'}
        </Typography>
      ),
    },
    {
      accessorKey: 'delivery_methods',
      header: 'delivery_methods',
      Cell: ({ cell }: any) => {
        const arr = Array.isArray(cell.getValue()) ? cell.getValue() : [];
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {arr.length ? (
              arr.map((d: string) => <Chip key={d} size="small" label={d} variant="outlined" />)
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Stack>
        );
      },
    },
    {
      accessorKey: 'stakeholder_emails',
      header: 'stakeholder_emails',
      Cell: ({ cell }: any) => {
        const arr = Array.isArray(cell.getValue()) ? cell.getValue() : [];
        return (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {arr.length ? arr.join(', ') : '—'}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'email_subject',
      header: 'email_subject',
      Cell: ({ cell }: any) => <Typography variant="body2">{cell.getValue() || '—'}</Typography>,
    },
    {
      accessorKey: 'email_format',
      header: 'email_format',
      Cell: ({ cell }: any) => <Typography variant="body2">{cell.getValue() || '—'}</Typography>,
    },
    {
      accessorKey: 'attachment_formats',
      header: 'attachment_formats',
      Cell: ({ cell }: any) => {
        const arr = Array.isArray(cell.getValue()) ? cell.getValue() : [];
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {arr.length ? (
              arr.map((d: string) => (
                <Chip key={d} size="small" label={d.toUpperCase()} variant="outlined" />
              ))
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Stack>
        );
      },
    },
    {
      accessorKey: 'actions',
      header: 'actions',
      enableSorting: false,
      Cell: ({ row }: any) => (
        <Tooltip title="Edit trigger">
          <IconButton
            size="small"
            onClick={() =>
              navigate(`/r/records/templates/${templateId}/triggers/edit/${row.original.id}`, {
                state: { templateName, editTriggerId: row.original.id },
              })
            }
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'Triggers' },
  ];
  return (
    <Box
      sx={{
        padding: '5px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item: { label: string; href: string }) => {
          if (item) {
            navigate(item?.href);
          }
        }}
      />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        {/* First row: Back button + Title + Add button */}
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(-1)} color="primary" size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', ml: 1 }}>
            Triggers — {templateName}
          </Typography>
        </Grid>

        <Grid item xs={6} textAlign="right">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() =>
              navigate(`/r/records/templates/${templateId}/triggers/create`, {
                state: { templateName },
              })
            }
          >
            Create Trigger
          </Button>
        </Grid>

        {/* Second row: Chips */}
        <Grid item xs={12} sx={{ mt: 1, mb: 2, ml: 3 }}>
          <Chip size="small" label={`Total: ${totalCount}`} sx={{ mr: 1 }} />
          <Chip size="small" color="success" variant="outlined" label={`Active: ${activeCount}`} />
        </Grid>
      </Grid>

      <Card variant="outlined">
        <CardContent>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
              <CircularProgress size={22} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Loading…
              </Typography>
            </Stack>
          ) : (
            <CommonTable
              columns={columns as any}
              data={rows}
              isLoading={loading}
              enableTopToolbar={false}
              enableBottomToolbar={true}
              enableTableFooter={false}
              enableRowSelection={false}
              getRowId={(row: TriggerApiItem) => row.id}
              muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 300px)', width: '100%' } }}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TriggerListPage;
